import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fmtDop } from "../helpers";

/**
 * AdminPayoutsManager — admin tool to:
 *  1) Pick a date range (defaults to last month) and a vendor.
 *  2) Compute gross sales for that vendor in that range via RPC.
 *  3) Create a vendor_payouts row with computed commission/net.
 *  4) Approve / mark as paid existing payouts.
 */
// `$` prop accepted for backwards compat but ignored: vendor payout amounts
// (gross_sales, net_amount, commission) are already in DOP — never convert.
export function AdminPayoutsManager({ lang }: { lang: string; $?: (n: number) => string }) {
  const $ = fmtDop;
  const [vendors, setVendors] = useState<any[]>([]);
  const [vendorId, setVendorId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [computed, setComputed] = useState<{ gross: number; orders: number; items: number } | null>(null);
  const [rate, setRate] = useState(0.15);
  const [busy, setBusy] = useState(false);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [filter, setFilter] = useState<string>("");

  // Default range = last month
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    setFrom(start.toISOString().slice(0, 10));
    setTo(end.toISOString().slice(0, 10));
  }, []);

  // Load vendor list (distinct vendor_id from products)
  const loadVendors = useCallback(async () => {
    const { data: prods } = await supabase.from("products").select("vendor_id").not("vendor_id", "is", null);
    const ids = [...new Set((prods || []).map((p: any) => p.vendor_id))];
    if (!ids.length) { setVendors([]); return; }
    const { data: profs } = await supabase.from("profiles").select("user_id, first_name, last_name, email").in("user_id", ids);
    const list = ids.map(id => {
      const p = (profs || []).find((x: any) => x.user_id === id);
      const name = p ? (`${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email || id.slice(0, 8)) : id.slice(0, 8);
      return { id, name };
    });
    setVendors(list);
    if (!vendorId && list[0]) setVendorId(list[0].id);
  }, [vendorId]);

  const loadPayouts = useCallback(async () => {
    let q = supabase.from("vendor_payouts").select("*").order("created_at", { ascending: false }).limit(60);
    if (filter) q = q.eq("status", filter);
    const { data } = await q;
    setPayouts(data || []);
  }, [filter]);

  useEffect(() => { loadVendors(); loadPayouts(); }, [loadVendors, loadPayouts]);

  // Load vendor commission rate when changed
  useEffect(() => {
    if (!vendorId) return;
    supabase.from("vendor_settings").select("commission_rate").eq("vendor_id", vendorId).maybeSingle().then(({ data }) => {
      if (data?.commission_rate !== undefined && data?.commission_rate !== null) setRate(Number(data.commission_rate));
    });
  }, [vendorId]);

  const compute = async () => {
    if (!vendorId || !from || !to) return;
    setBusy(true);
    const { data, error } = await supabase.rpc("compute_vendor_sales", { _vendor_id: vendorId, _from: from, _to: to });
    setBusy(false);
    if (error) { alert(error.message); return; }
    const row = (data as any[])?.[0];
    setComputed({ gross: Number(row?.gross_sales || 0), orders: Number(row?.order_count || 0), items: Number(row?.item_count || 0) });
  };

  const createPayout = async () => {
    if (!computed || !vendorId) return;
    const gross = computed.gross;
    const commission = +(gross * rate).toFixed(2);
    const net = +(gross - commission).toFixed(2);
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("vendor_payouts").insert({
      vendor_id: vendorId, period_start: from, period_end: to,
      gross_sales: gross, commission_rate: rate, commission_amount: commission,
      net_amount: net, status: "pending", created_by: u.user?.id || null,
    });
    if (error) { alert(error.message); return; }
    setComputed(null); loadPayouts();
  };

  const updateStatus = async (id: string, status: string, extra: any = {}) => {
    const { error } = await supabase.from("vendor_payouts").update({ status, ...extra }).eq("id", id);
    if (error) { alert(error.message); return; }
    loadPayouts();
  };

  const markPaid = (p: any) => {
    const ref = prompt(lang === "es" ? "Referencia / nº transferencia:" : "Reference / transfer no:", p.reference || "");
    if (ref === null) return;
    updateStatus(p.id, "paid", { paid_at: new Date().toISOString(), reference: ref || null });
  };

  const summary = useMemo(() => {
    return payouts.reduce((acc, p) => {
      acc.total += Number(p.net_amount) || 0;
      acc[p.status] = (acc[p.status] || 0) + (Number(p.net_amount) || 0);
      return acc;
    }, { total: 0, pending: 0, approved: 0, paid: 0, cancelled: 0 } as any);
  }, [payouts]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="ast">
        <div className="as"><div className="lbl">{lang === "es" ? "Total emitido" : "Total issued"}</div><div className="val">{$(summary.total)}</div></div>
        <div className="as"><div className="lbl">{lang === "es" ? "Pendiente" : "Pending"}</div><div className="val" style={{ color: "var(--am)" }}>{$(summary.pending + summary.approved)}</div></div>
        <div className="as"><div className="lbl">{lang === "es" ? "Pagado" : "Paid"}</div><div className="val" style={{ color: "var(--ac)" }}>{$(summary.paid)}</div></div>
      </div>

      {/* Generator */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: 10, padding: 14 }}>
        <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 14 }}>{lang === "es" ? "Generar pago" : "Generate payout"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
          <label style={lbl}>{lang === "es" ? "Vendor" : "Vendor"}
            <select value={vendorId} onChange={e => setVendorId(e.target.value)} style={fieldStyle}>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <label style={lbl}>{lang === "es" ? "Desde" : "From"}
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={fieldStyle} />
          </label>
          <label style={lbl}>{lang === "es" ? "Hasta" : "To"}
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={fieldStyle} />
          </label>
          <label style={lbl}>{lang === "es" ? "Comisión" : "Rate"}
            <input type="number" step="0.005" min="0" max="1" value={rate} onChange={e => setRate(Number(e.target.value))} style={fieldStyle} />
          </label>
          <button className="btn bg" disabled={busy || !vendorId} onClick={compute} style={{ fontSize: 11 }}>
            {busy ? "…" : (lang === "es" ? "Calcular" : "Compute")}
          </button>
        </div>

        {computed && (
          <div style={{ marginTop: 12, padding: 10, background: "var(--bg)", borderRadius: 8, border: "1px solid var(--bd2)", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 11 }}>
              <strong>{$(computed.gross)}</strong> {lang === "es" ? "bruto" : "gross"} • {computed.orders} {lang === "es" ? "órd." : "ord."} • {computed.items} {lang === "es" ? "uds." : "u."}
            </div>
            <div style={{ fontSize: 11, color: "var(--am)" }}>-{$(computed.gross * rate)} {lang === "es" ? "comisión" : "commission"}</div>
            <div style={{ fontSize: 12, color: "var(--ac)", fontWeight: 700 }}>{$(computed.gross * (1 - rate))} {lang === "es" ? "neto" : "net"}</div>
            <div style={{ flex: 1 }} />
            <button className="btn bp bs" onClick={createPayout} style={{ fontSize: 11 }}>
              {lang === "es" ? "Crear payout" : "Create payout"}
            </button>
          </div>
        )}
      </div>

      {/* List */}
      <div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, margin: 0, flex: 1 }}>{lang === "es" ? "Pagos generados" : "Generated payouts"}</h3>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={fieldStyle}>
            <option value="">{lang === "es" ? "Todos" : "All"}</option>
            <option value="pending">pending</option>
            <option value="approved">approved</option>
            <option value="paid">paid</option>
            <option value="cancelled">cancelled</option>
          </select>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>{lang === "es" ? "Vendor" : "Vendor"}</th>
              <th>{lang === "es" ? "Periodo" : "Period"}</th>
              <th>{lang === "es" ? "Bruto" : "Gross"}</th>
              <th>%</th>
              <th>{lang === "es" ? "Neto" : "Net"}</th>
              <th>{lang === "es" ? "Estado" : "Status"}</th>
              <th>{lang === "es" ? "Acciones" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map(p => {
              const v = vendors.find(x => x.id === p.vendor_id);
              return (
                <tr key={p.id}>
                  <td style={{ fontSize: 11 }}>{v?.name || p.vendor_id.slice(0, 8)}</td>
                  <td style={{ fontSize: 10.5 }}>{p.period_start} → {p.period_end}</td>
                  <td>{$(p.gross_sales)}</td>
                  <td style={{ fontSize: 10 }}>{(Number(p.commission_rate) * 100).toFixed(1)}%</td>
                  <td style={{ fontWeight: 600 }}>{$(p.net_amount)}</td>
                  <td><span className={`stb ${p.status}`} style={{ fontSize: 10 }}>{p.status}</span></td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      {p.status === "pending" && <button className="btn bg bs" style={miniBtn} onClick={() => updateStatus(p.id, "approved")}>✓ {lang === "es" ? "Aprobar" : "Approve"}</button>}
                      {p.status !== "paid" && p.status !== "cancelled" && <button className="btn bp bs" style={miniBtn} onClick={() => markPaid(p)}>💸 {lang === "es" ? "Marcar pagado" : "Mark paid"}</button>}
                      {p.status !== "cancelled" && p.status !== "paid" && <button className="btn bo bs" style={miniBtn} onClick={() => { if (confirm(lang === "es" ? "¿Cancelar este pago?" : "Cancel this payout?")) updateStatus(p.id, "cancelled"); }}>✕</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {payouts.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 30, color: "var(--tx3)" }}>
                {lang === "es" ? "Sin payouts todavía." : "No payouts yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  display: "block", marginTop: 4, width: "100%",
  padding: "6px 8px", fontSize: 12, border: "1px solid var(--bd2)",
  borderRadius: 6, background: "var(--bg)", color: "var(--tx)",
};
const lbl: React.CSSProperties = { fontSize: 10, color: "var(--tx3)", fontWeight: 600, display: "block" };
const miniBtn: React.CSSProperties = { fontSize: 9, padding: "2px 6px", height: "auto" };